import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { Institution, InstitutionApiGetResponse } from '../models/institutions.model';
import { Observable, forkJoin, of } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root',
})
export class InstitutionService {
	private readonly INSTITUTION_PAGE_SIZE: number = 3000;
	private readonly DEFAULT_INSTITUTION_VERSION: string = 'v1';

	institutions$: Observable<Institution[]>;

	constructor(private httpClient: HttpClient) {
		this.institutions$ = this.fetchInstitutions().pipe(shareReplay(1));
	}

	getInstitutionVersion(institution: Institution): string {
		const versions: string[] | Record<string, string> | Array<{ id: string; description: string }> | undefined = institution?.metadata?.versions;

		if (!versions) {
			return this.DEFAULT_INSTITUTION_VERSION;
		}

		if (Array.isArray(versions)) {
			const last: string | { id: string; description: string } | undefined = versions.at(-1);
			if (typeof last === 'object' && last !== null && 'id' in last) {
				return last.id;
			}
			return last || this.DEFAULT_INSTITUTION_VERSION;
		}

		const keys: string[] = Object.keys(versions);
		return keys.at(-1) || this.DEFAULT_INSTITUTION_VERSION;
	}

	private fetchInstitutions(): Observable<Institution[]> {
		const url: string = environment.trovataApi + '/data/institutions/all';
		let params: HttpParams = new HttpParams({
			fromObject: {
				from: 0,
				size: this.INSTITUTION_PAGE_SIZE,
			},
		});

		if (environment.production) {
			params = params.set('beta', false);
		}

		return this.httpClient.get<InstitutionApiGetResponse>(url, { params }).pipe(
			switchMap((firstResponse: InstitutionApiGetResponse) => {
				const requests: Observable<InstitutionApiGetResponse>[] = [of(firstResponse)];

				for (let i: number = this.INSTITUTION_PAGE_SIZE; i < firstResponse.total; i += this.INSTITUTION_PAGE_SIZE) {
					requests.push(this.httpClient.get<InstitutionApiGetResponse>(url, { params: params.set('from', i) }));
				}

				return forkJoin(requests);
			}),
			map((responses: InstitutionApiGetResponse[]) =>
				responses
					.flatMap((response: InstitutionApiGetResponse) => response.institutions)
					.map((institution: Institution) => {
						if (!institution.institutionNickname) {
							institution.institutionNickname = institution.institutionName;
						}

						return institution;
					})
			)
		);
	}
}
